import { useMemo, useState, useEffect } from 'react';

import { FileStatus, FileType } from 'services/file.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { useLocation, Path } from 'components/_hooks/use-router';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { selectSelectedFile } from 'components/app/_mediator/app-files.slice';
import { selectStats } from 'components/app/editor/_mediator/editor.slice';
import { AddClipAction, setAddClipAction } from 'components/app/editor/_mediator/editor-actions.slice';
import { useClipsAdd } from 'components/app/editor/_mediator/editor-clips/use-clips-add';
import { useClipsAddImg } from 'components/app/editor/_mediator/editor-clips/use-clips-add-img';
import { useEditorCb } from 'components/app/editor/_mediator/use-editor-cb';
import { useClipsTimestamp } from 'components/app/editor/_mediator/editor-clips/use-clips-timestamp';
import { setPlayerSeekEnabled } from 'components/app/storyboard/_mediator/sb.slice';

import { AppButton } from 'components/app/app-components/app-button/app-button';
import { HoverBtn } from 'components/app/app-components/hover-btn/hover-btn';
import { ActionsBtnOpt } from './actions-btn-option';

export const ActionsAddClip = () => {
  const { path } = useLocation();
  const { getPlayerRef } = useEditorCb();

  const beginnerMode = useAppSelector((state) => state.app.beginnerMode);
  const isEmptyCell = useAppSelector((state) => state.sb__player.isEmptyCell);
  const tsLinkedPayload = useAppSelector((state) => state.timestamp.tsLinkedPayload);
  const addClipAction = useAppSelector((state) => state.editor__actions.addClipAction);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const isAudioOn = useAppSelector((state) => state.audio.isAudioOn);
  const selectedFile = useAppSelector(selectSelectedFile);
  const stats = useAppSelector(selectStats);
  const dispatch = useAppDispatch();

  const [isEnable, setIsEnable] = useState(false);
  const [isHover, setIsHover] = useState(false);

  const timestampClip = useClipsTimestamp(activeClip ?? undefined);
  const addImg = useClipsAddImg();
  const addClip = useClipsAdd();

  const isSb = useMemo(() => {
    return path === Path.storyboard;
  }, [path]);

  const position: 'start' | 'end' | null = useMemo(() => {
    if (!isHover || !isEnable) return null;
    const player = getPlayerRef();
    if (!player) return null;
    const duration = player.getDuration();
    const playerTime = player.getCurrentTime();
    if (playerTime === 0) return 'start';
    else if (playerTime === duration) return 'end';
    return null;
  }, [isHover, isEnable]);

  const isDisabled = !(selectedFile && isEnable);

  const tip = isDisabled
    ? 'Select a media file in Highlights'
    : isSb
    ? 'Add a clip at current player moment'
    : 'Seek the player to find highlights, then tag them';

  const onActive = (isHover: boolean) => {
    setIsHover(isHover);
    if (isSb) dispatch(setPlayerSeekEnabled(isHover));
  };

  const onSplit = () => {
    dispatch(setAddClipAction('split'));
    if (!clipsMap) return;
    if (tsLinkedPayload) {
      const { isFirstInGroup, isLastInGroup, group } = tsLinkedPayload;
      if (
        (isFirstInGroup && selectClipData(group[0], clipsMap).startTime === null) ||
        (isLastInGroup && selectClipData(group[group.length - 1], clipsMap).endTime === null)
      )
        handleAdd('split');
      else timestampClip('split');
    } else handleAdd('split');
  };

  const handleAdd = (action: AddClipAction) => {
    dispatch(setAddClipAction(action));
    if (selectedFile?.fileType === FileType.image) addImg();
    else addClip(action);
  };

  const onAction = () => {
    switch (addClipAction) {
      case 'startTime':
        handleAdd('startTime');
        break;
      case 'endTime':
        handleAdd('endTime');
        break;
      case 'split':
        onSplit();
        break;
    }
  };

  useEffect(() => {
    if (!stats) return;
    if (selectedFile?.status === FileStatus.Error) setIsEnable(false);
    else if (stats.available === 0) setIsEnable(false);
    else if (path === Path.selector) setIsEnable(true);
    else {
      if (isAudioOn || isEmptyCell) setIsEnable(false);
      else setIsEnable(true);
    }
  }, [path, isAudioOn, isEmptyCell, stats, selectedFile?.status]);

  if (beginnerMode) {
    return (
      <AppButton
        onClick={() => handleAdd('startTime')}
        disabled={isDisabled}
        data-intro={IntroKeys.srAddClip}
        tip={tip}
        tipPosition="bottom"
      >
        Add {isSb ? 'Clip' : 'Tag'}
      </AppButton>
    );
  }

  return (
    <HoverBtn
      button={
        <AppButton
          onClick={onAction}
          disabled={!(selectedFile && isEnable)}
          onMouseEnter={() => onActive(true)}
          onMouseLeave={() => onActive(false)}
          data-intro={IntroKeys.srAddClip}
          tip={tip}
          tipPosition="bottom"
        >
          Add {isSb ? 'Clip' : 'Tag'}
        </AppButton>
      }
      isEnabled={selectedFile && isEnable}
    >
      <ActionsBtnOpt
        title="Beginning"
        desc="Add Tag at start of a highlight"
        onClick={() => handleAdd('startTime')}
        selected={addClipAction === 'startTime'}
        disabled={position === 'end'}
      />
      <ActionsBtnOpt
        title="End"
        desc="Add Tag at end of a highlight"
        onClick={() => handleAdd('endTime')}
        selected={addClipAction === 'endTime'}
        disabled={position === 'start'}
      />
      <ActionsBtnOpt
        title="Middle"
        desc="Add Tag in the middle of a highlight"
        selected={addClipAction === 'split'}
        disabled={position !== null}
        onClick={onSplit}
      />
    </HoverBtn>
  );
};
